import ReactMarkdown from 'react-markdown';
import { LuCircleCheck } from 'react-icons/lu';
import { DifficultyLabel } from '../../ui/Labels';
import { useStrings } from '../../tenant/TenantProvider';
import { asText, formatMath, parseDescription } from './statementFormat';

const MARKDOWN_COMPONENTS = {
  a: ({ children, href }) => (
    <a className="text-link" href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
};

function Markdown({ children }) {
  if (typeof children !== 'string' || !children.trim()) return null;
  return <ReactMarkdown components={MARKDOWN_COMPONENTS}>{formatMath(children)}</ReactMarkdown>;
}

export function ProblemStatement({ title, number, difficulty, meta = [], solved, description, topics = [], children }) {
  const t = useStrings();
  const { blocks, examples, constraints } = parseDescription(description);

  return (
    <article className="statement">
      <header className="statement__header">
        <h1 className="statement__title">
          {number && <span className="statement__number">{number}</span>}
          {title}
        </h1>
        <div className="statement__meta">
          {difficulty && <DifficultyLabel level={difficulty} />}
          {meta.map((item) => (
            <span key={item} className="statement__meta-item">
              {item}
            </span>
          ))}
          {solved && (
            <span className="statement__solved">
              <LuCircleCheck aria-hidden="true" />
              {t('workspace.solved')}
            </span>
          )}
        </div>
      </header>

      <div className="statement__body">
        {blocks.length === 0 ? (
          <p className="muted">{t('workspace.noStatement')}</p>
        ) : (
          blocks.map((block, index) => (
            <div key={index} className="statement__block">
              <Markdown>{block.text ?? block.value}</Markdown>
              {(block.img || block.type === 'img') && (
                <img className="statement__figure" src={block.img ?? block.value} alt="" loading="lazy" />
              )}
            </div>
          ))
        )}
      </div>

      {examples.map((example, index) => (
        <section key={index} className="statement__example">
          <h2 className="statement__heading">{t('workspace.example', { number: index + 1 })}</h2>
          <dl className="example">
            <dt>{t('workspace.input')}</dt>
            <dd>
              <pre>{asText(example.input)}</pre>
            </dd>
            <dt>{t('workspace.output')}</dt>
            <dd>
              <pre>{asText(example.output)}</pre>
            </dd>
            {example.explanation && (
              <>
                <dt>{t('workspace.explanation')}</dt>
                <dd className="example__explanation">
                  <Markdown>{asText(example.explanation)}</Markdown>
                </dd>
              </>
            )}
          </dl>
          {example.img && <img className="statement__figure" src={example.img} alt="" loading="lazy" />}
        </section>
      ))}

      {constraints.length > 0 && (
        <section className="statement__section">
          <h2 className="statement__heading">{t('workspace.constraints')}</h2>
          <ul className="statement__constraints">
            {constraints.map((constraint, index) => (
              <li key={index}>
                <Markdown>{asText(constraint)}</Markdown>
              </li>
            ))}
          </ul>
        </section>
      )}

      {topics.length > 0 && (
        <details className="statement__topics">
          <summary>{t('workspace.topics')}</summary>
          <ul>
            {topics.map((topic) => (
              <li key={topic} className="badge">
                {topic}
              </li>
            ))}
          </ul>
        </details>
      )}

      {children}
    </article>
  );
}
